import React, { useMemo, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
} from "@mui/material";
import {
  Iconography,
  IconographySizes,
} from "@hid-galaxy-ui/galaxy-react";
import moment from "moment";
import { useAppDispatch, useAppSelector } from "../../../../../../../../redux";
import { getValidationHistorySagaAction } from "../../../../../../../../redux/saga/validation-documentation/saga-actions";
import type { HistoryEntryType } from "../../../../../../../../redux/slices/validation-documentation";
import "./ValidationHistoryList.scss";

/** Partner-only row → partner date; after admin verification → admin date. */
const resolveLastUpdatedOn = (entry: HistoryEntryType): string => {
  const status = entry.validation_status?.trim() ?? "";
  if (status === "Submitted") {
    return entry.partner_submission_date ?? entry.created_at;
  }
  return entry.admin_submission_date ?? entry.created_at;
};

const ValidationHistoryList: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { integrationId } = useParams<{ integrationId: string }>();
  const { historyData, status } = useAppSelector(
    (state) => state.validation_documentation
  );

  // Fetch validation history when integrationId is available
  useEffect(() => {
    if (integrationId) {
      dispatch(getValidationHistorySagaAction(Number(integrationId)));
    }
  }, [integrationId, dispatch]);

  // Map API history data to component format
  const mappedHistoryData = useMemo(() => {
    return historyData.map((entry) => ({
      id: entry.documentation_id.toString(),
      date: resolveLastUpdatedOn(entry),
      validationStatus: entry.validation_status as
        | "Submitted"
        | "Validation - Failed"
        | "Validated - Full approval"
        | "Validated - Conditional approval",
      validationScore: entry.validation_score,
      endorsementLevel: entry.endorsement_level,
      documentation_id: entry.documentation_id,
    }));
  }, [historyData]);

  const handleBackToValidation = () => {
    navigate(`..`);
  };

  const handleViewEntry = (documentationId: number) => {
    navigate(`${documentationId}`);
  };

  const formatDate = (dateString: string) => {
    const m = moment(dateString);
    return m.isValid() ? m.format("DD/MM/YYYY") : "—";
  };

  const getStatusChip = (
    status:
      | "Submitted"
      | "Validation - Failed"
      | "Validated - Full approval"
      | "Validated - Conditional approval"
      | string
  ) => {
    let className = "";
    switch (status) {
      case "Submitted":
        className = "status-submitted";
        break;
      case "Validation - Failed":
        className = "status-rejected";
        break;
      case "Validated - Full approval":
        className = "status-approved";
        break;
      case "Validated - Conditional approval":
        className = "status-approved";
        break;
      default:
        className = "status-submitted";
    }
    return <Chip label={status} className={`status-chip ${className}`} />;
  };

  return (
    <div className="validation-history-list-container">
      <div className="history-content-wrapper">
        <div className="history-header">
          <h2>Validation history</h2>
        </div>

        <button
          type="button"
          className="back-link"
          onClick={handleBackToValidation}
        >
          <Iconography icon="arrowLeft" size={IconographySizes.Small} />
          <span>BACK TO VALIDATION DETAILS</span>
        </button>

        <div className="history-table-area">
          <TableContainer component={Paper} className="history-table">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Last Updated On</TableCell>
                  <TableCell>Validation Status</TableCell>
                  <TableCell>Validation Score</TableCell>
                  <TableCell>Endorsement Level</TableCell>
                  <TableCell>View Details</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {status.historyLoading ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      Loading...
                    </TableCell>
                  </TableRow>
                ) : mappedHistoryData.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      No validation history found
                    </TableCell>
                  </TableRow>
                ) : (
                  mappedHistoryData.map((entry) => (
                    <TableRow key={entry.id}>
                      <TableCell>{formatDate(entry.date)}</TableCell>
                      <TableCell>
                        {getStatusChip(entry.validationStatus)}
                      </TableCell>
                      <TableCell>{entry.validationScore}</TableCell>
                      <TableCell>{entry.endorsementLevel}</TableCell>
                      <TableCell>
                        <button
                          type="button"
                          className="view-link"
                          onClick={() =>
                            handleViewEntry(entry.documentation_id)
                          }
                        >
                          View
                        </button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </div>
      </div>
    </div>
  );
};

export default ValidationHistoryList;
